import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../config/supabase";
import { User } from "@supabase/supabase-js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getGoalForDate, getLocalDateString } from "../utils/goalUtils";

interface ProgressReportProps {
  user: User | null;
  darkMode: boolean;
}

interface DailyLog {
  date: string;
  time_spent_minutes: number;
  daily_goal_at_time?: number | null;
  weekly_goal_at_time?: number | null;
}

interface ChildLog extends DailyLog {
  child_id: string;
}

interface GoalColumns {
  weekly_goal_minutes: number;
  monday_goal_minutes?: number | null;
  tuesday_goal_minutes?: number | null;
  wednesday_goal_minutes?: number | null;
  thursday_goal_minutes?: number | null;
  friday_goal_minutes?: number | null;
  saturday_goal_minutes?: number | null;
  sunday_goal_minutes?: number | null;
}

interface UserProfile {
  name?: string;
  surname?: string;
  treatment_type?: string;
  created_at?: string;
}

interface ChildRecord {
  id: string;
  name: string;
}

const PDF_LIMIT_KEY = "amblyfy_pdf_report_usage";
const MAX_REPORTS_PER_DAY = 5;

function getPdfUsageToday(): number {
  try {
    const stored = localStorage.getItem(PDF_LIMIT_KEY);
    if (!stored) return 0;
    const parsed = JSON.parse(stored);
    if (parsed.date !== getLocalDateString()) return 0;
    return parsed.count || 0;
  } catch {
    return 0;
  }
}

function incrementPdfUsage(): void {
  const today = getLocalDateString();
  const current = getPdfUsageToday();
  localStorage.setItem(
    PDF_LIMIT_KEY,
    JSON.stringify({ date: today, count: current + 1 })
  );
}

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Compute stats + rows for a set of logs against goals over a date range. */
function computeStats(
  logsByDate: Record<string, DailyLog[]>,
  allDates: string[],
  goals: GoalColumns
) {
  let totalMinutes = 0;
  let totalSessions = 0;
  let goalsHit = 0;
  let trainingDays = 0;
  let activeDays = 0;
  let longestStreak = 0;
  let tempStreak = 0;

  const dailyRows: Array<{
    date: string;
    minutes: number;
    goal: string;
    achieved: string;
    sessions: number;
  }> = [];

  for (const dateStr of allDates) {
    const dayLogs = logsByDate[dateStr] || [];
    const minutes = dayLogs.reduce(
      (sum, l) => sum + (l.time_spent_minutes || 0),
      0
    );
    const sessions = dayLogs.length;

    let goal: number | null = null;
    if (dayLogs.length > 0 && dayLogs[0].daily_goal_at_time !== undefined) {
      goal = dayLogs[0].daily_goal_at_time ?? null;
    } else {
      goal = getGoalForDate(goals, dateStr);
    }

    const isRestDay = goal === null;

    totalMinutes += minutes;
    totalSessions += sessions;

    if (!isRestDay) {
      trainingDays++;
      if (goal && goal > 0 && minutes >= goal) goalsHit++;
    }

    if (minutes > 0) {
      activeDays++;
      tempStreak++;
      if (tempStreak > longestStreak) longestStreak = tempStreak;
    } else if (!isRestDay) {
      tempStreak = 0;
    }

    const goalStr = isRestDay ? "Rest Day" : goal ? formatMinutes(goal) : "—";
    const achievedStr = isRestDay
      ? "—"
      : goal && goal > 0
      ? `${Math.min(Math.round((minutes / goal) * 100), 999)}%`
      : "—";

    dailyRows.push({ date: dateStr, minutes, goal: goalStr, achieved: achievedStr, sessions });
  }

  const currentStreak = tempStreak;
  const avgSession = totalSessions > 0 ? Math.round(totalMinutes / totalSessions) : 0;
  const goalAchievementRate = trainingDays > 0 ? Math.round((goalsHit / trainingDays) * 100) : 0;
  const consistencyRate = allDates.length > 0 ? Math.round((activeDays / allDates.length) * 100) : 0;

  // Weekly summaries
  const weeklySummaries: Array<{
    weekLabel: string;
    totalMinutes: number;
    sessions: number;
    goalsHit: number;
    trainingDays: number;
  }> = [];

  if (allDates.length > 0) {
    const startD = new Date(allDates[0] + "T00:00:00");
    const endD = new Date(allDates[allDates.length - 1] + "T00:00:00");
    let weekStart = new Date(startD);
    while (weekStart <= endD) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      if (weekEnd > endD) weekEnd.setTime(endD.getTime());

      let wMinutes = 0;
      let wSessions = 0;
      let wGoalsHit = 0;
      let wTrainingDays = 0;

      const d = new Date(weekStart);
      while (d <= weekEnd) {
        const ds = getLocalDateString(d);
        const dLogs = logsByDate[ds] || [];
        const mins = dLogs.reduce((sum, l) => sum + (l.time_spent_minutes || 0), 0);
        let g: number | null = null;
        if (dLogs.length > 0 && dLogs[0].daily_goal_at_time !== undefined) {
          g = dLogs[0].daily_goal_at_time ?? null;
        } else {
          g = getGoalForDate(goals, ds);
        }
        wMinutes += mins;
        wSessions += dLogs.length;
        if (g !== null) {
          wTrainingDays++;
          if (g > 0 && mins >= g) wGoalsHit++;
        }
        d.setDate(d.getDate() + 1);
      }

      weeklySummaries.push({
        weekLabel: `${getLocalDateString(weekStart)} — ${getLocalDateString(weekEnd)}`,
        totalMinutes: wMinutes,
        sessions: wSessions,
        goalsHit: wGoalsHit,
        trainingDays: wTrainingDays,
      });

      weekStart.setDate(weekStart.getDate() + 7);
    }
  }

  return {
    totalMinutes, totalSessions, goalsHit, trainingDays, activeDays,
    currentStreak, longestStreak, avgSession, goalAchievementRate, consistencyRate,
    dailyRows, weeklySummaries,
  };
}

/** Render a statistics box on the PDF. Returns new yPos. */
function renderStatsBox(
  doc: jsPDF,
  yPos: number,
  stats: ReturnType<typeof computeStats>,
  brandCyan: [number, number, number]
): number {
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setDrawColor(...brandCyan);
  doc.setLineWidth(0.5);
  doc.rect(14, yPos, pageWidth - 28, 32, "S");

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  const c1 = 20, c2 = 75, c3 = 130;
  yPos += 7;

  doc.setFont("helvetica", "bold"); doc.text("Total Time:", c1, yPos);
  doc.setFont("helvetica", "normal"); doc.text(formatMinutes(stats.totalMinutes), c1 + 30, yPos);
  doc.setFont("helvetica", "bold"); doc.text("Sessions:", c2, yPos);
  doc.setFont("helvetica", "normal"); doc.text(String(stats.totalSessions), c2 + 25, yPos);
  doc.setFont("helvetica", "bold"); doc.text("Avg Session:", c3, yPos);
  doc.setFont("helvetica", "normal"); doc.text(formatMinutes(stats.avgSession), c3 + 30, yPos);

  yPos += 8;
  doc.setFont("helvetica", "bold"); doc.text("Goal Rate:", c1, yPos);
  doc.setFont("helvetica", "normal"); doc.text(`${stats.goalAchievementRate}%`, c1 + 30, yPos);
  doc.setFont("helvetica", "bold"); doc.text("Consistency:", c2, yPos);
  doc.setFont("helvetica", "normal"); doc.text(`${stats.consistencyRate}%`, c2 + 25, yPos);
  doc.setFont("helvetica", "bold"); doc.text("Active Days:", c3, yPos);
  doc.setFont("helvetica", "normal"); doc.text(`${stats.activeDays} / ${stats.dailyRows.length}`, c3 + 30, yPos);

  yPos += 8;
  doc.setFont("helvetica", "bold"); doc.text("Goals Hit:", c1, yPos);
  doc.setFont("helvetica", "normal"); doc.text(`${stats.goalsHit} / ${stats.trainingDays}`, c1 + 30, yPos);
  doc.setFont("helvetica", "bold"); doc.text("Current Streak:", c2, yPos);
  doc.setFont("helvetica", "normal"); doc.text(`${stats.currentStreak} days`, c2 + 30, yPos);
  doc.setFont("helvetica", "bold"); doc.text("Longest Streak:", c3, yPos);
  doc.setFont("helvetica", "normal"); doc.text(`${stats.longestStreak} days`, c3 + 30, yPos);

  return yPos + 15;
}

/** Render weekly summaries table. Returns new yPos. */
function renderWeeklySummaries(
  doc: jsPDF,
  yPos: number,
  weeklySummaries: ReturnType<typeof computeStats>["weeklySummaries"],
  brandBlue: [number, number, number]
): number {
  if (weeklySummaries.length === 0) return yPos;

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Weekly Summaries", 14, yPos);
  yPos += 2;

  autoTable(doc, {
    startY: yPos,
    head: [["Week", "Total Time", "Sessions", "Goals Hit", "Training Days"]],
    body: weeklySummaries.map((w) => [
      w.weekLabel,
      formatMinutes(w.totalMinutes),
      String(w.sessions),
      `${w.goalsHit} / ${w.trainingDays}`,
      String(w.trainingDays),
    ]),
    headStyles: { fillColor: brandBlue, textColor: [255, 255, 255], fontSize: 8, fontStyle: "bold" },
    bodyStyles: { fontSize: 8 },
    alternateRowStyles: { fillColor: [240, 248, 255] },
    margin: { left: 14, right: 14 },
    theme: "grid",
  });

  return (doc as any).lastAutoTable.finalY + 10;
}

/** Render daily activity table. Returns new yPos. */
function renderDailyTable(
  doc: jsPDF,
  yPos: number,
  dailyRows: ReturnType<typeof computeStats>["dailyRows"],
  brandBlue: [number, number, number],
  brandGreen: [number, number, number]
): number {
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  if (yPos > 250) { doc.addPage(); yPos = 20; }
  doc.text("Daily Activity Log", 14, yPos);
  yPos += 2;

  autoTable(doc, {
    startY: yPos,
    head: [["Date", "Day", "Time", "Goal", "Achievement", "Sessions"]],
    body: dailyRows.map((row) => {
      const d = new Date(row.date + "T00:00:00");
      const dayName = d.toLocaleDateString("en-US", { weekday: "short" });
      return [
        row.date, dayName,
        row.minutes > 0 ? formatMinutes(row.minutes) : "—",
        row.goal, row.achieved, String(row.sessions),
      ];
    }),
    headStyles: { fillColor: brandBlue, textColor: [255, 255, 255], fontSize: 8, fontStyle: "bold" },
    bodyStyles: { fontSize: 7 },
    alternateRowStyles: { fillColor: [240, 248, 255] },
    margin: { left: 14, right: 14 },
    theme: "grid",
    didParseCell: (data: any) => {
      if (data.section === "body" && data.column.index === 4) {
        const val = data.cell.raw as string;
        if (val === "—") return;
        const pct = parseInt(val);
        if (pct >= 100) { data.cell.styles.textColor = [34, 197, 94]; data.cell.styles.fontStyle = "bold"; }
        else if (pct >= 75) data.cell.styles.textColor = [74, 222, 128];
        else if (pct >= 50) data.cell.styles.textColor = [250, 204, 21];
        else if (pct >= 25) data.cell.styles.textColor = [251, 146, 60];
        else data.cell.styles.textColor = [248, 113, 113];
      }
      if (data.section === "body" && data.column.index === 3) {
        if (data.cell.raw === "Rest Day") {
          data.cell.styles.textColor = brandGreen;
          data.cell.styles.fontStyle = "italic";
        }
      }
    },
  });

  return (doc as any).lastAutoTable.finalY + 10;
}

const ProgressReport: React.FC<ProgressReportProps> = ({ user, darkMode }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [firstEntryDate, setFirstEntryDate] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [reportsUsedToday, setReportsUsedToday] = useState(getPdfUsageToday());
  const [profile, setProfile] = useState<UserProfile | null>(null);

  // Fetch first entry date and profile
  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      // Fetch user profile (treatment_type determines self vs child mode)
      const { data: profileData } = await supabase
        .from("user_data")
        .select("name, surname, treatment_type, created_at")
        .eq("user_id", user.id)
        .single();

      if (profileData) setProfile(profileData);

      const isChild = profileData?.treatment_type === "child";

      // Fetch first log entry date (from daily_logs for self, child_logs for child)
      let earliest: string | null = null;

      if (!isChild) {
        const { data: firstLog } = await supabase
          .from("daily_logs")
          .select("date")
          .eq("user_id", user.id)
          .order("date", { ascending: true })
          .limit(1);
        if (firstLog && firstLog.length > 0) earliest = firstLog[0].date;
      } else {
        // Get all children, then find earliest child log
        const { data: childRows } = await supabase
          .from("children")
          .select("id")
          .eq("user_id", user.id);
        const childIds = (childRows || []).map((c) => c.id);
        if (childIds.length > 0) {
          const { data: firstLog } = await supabase
            .from("child_logs")
            .select("date")
            .in("child_id", childIds)
            .order("date", { ascending: true })
            .limit(1);
          if (firstLog && firstLog.length > 0) earliest = firstLog[0].date;
        }
      }

      if (earliest) {
        setFirstEntryDate(earliest);
        if (!startDate) setStartDate(earliest);
      }

      if (!endDate) setEndDate(getLocalDateString());
    };

    fetchData();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateDates = useCallback((): string | null => {
    if (!startDate || !endDate) return "Please select both start and end dates.";
    const today = getLocalDateString();
    if (endDate > today) return "End date cannot be in the future.";
    if (firstEntryDate && startDate < firstEntryDate)
      return `Start date cannot be before your first entry (${firstEntryDate}).`;
    if (startDate > endDate) return "Start date must be before end date.";
    if (reportsUsedToday >= MAX_REPORTS_PER_DAY)
      return `You have reached the daily limit of ${MAX_REPORTS_PER_DAY} reports. Please try again tomorrow.`;
    return null;
  }, [startDate, endDate, firstEntryDate, reportsUsedToday]);

  const generateReport = async () => {
    const validationError = validateDates();
    if (validationError) {
      setError(validationError);
      setSuccess("");
      return;
    }

    setGenerating(true);
    setError("");
    setSuccess("");

    try {
      const isChildMode = profile?.treatment_type === "child";

      // Generate all dates in range
      const allDates: string[] = [];
      const current = new Date(startDate + "T00:00:00");
      const end = new Date(endDate + "T00:00:00");
      while (current <= end) {
        allDates.push(getLocalDateString(current));
        current.setDate(current.getDate() + 1);
      }

      // Brand colors
      const brandBlue: [number, number, number] = [20, 68, 117];
      const brandCyan: [number, number, number] = [0, 174, 194];
      const brandGreen: [number, number, number] = [117, 188, 126];

      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();

      // Header
      doc.setFillColor(...brandBlue);
      doc.rect(0, 0, pageWidth, 35, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont("helvetica", "bold");
      doc.text("Amblyfy Progress Report", 14, 18);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const userName = profile
        ? `${profile.name || ""} ${profile.surname || ""}`.trim() || user!.email || "User"
        : user!.email || "User";
      doc.text(`Patient: ${userName}`, 14, 27);
      doc.text(`Period: ${startDate} to ${endDate}`, pageWidth - 14, 27, { align: "right" });
      doc.text(
        `Generated: ${getLocalDateString()} at ${new Date().toLocaleTimeString()}`,
        pageWidth - 14, 32, { align: "right" }
      );

      let yPos = 45;

      if (!isChildMode) {
        // ====== SELF MODE ======
        const { data: logs, error: logsError } = await supabase
          .from("daily_logs")
          .select("date, time_spent_minutes, daily_goal_at_time, weekly_goal_at_time")
          .eq("user_id", user!.id)
          .gte("date", startDate)
          .lte("date", endDate)
          .order("date", { ascending: true });

        if (logsError) throw logsError;

        const { data: goalsData } = await supabase
          .from("user_goals")
          .select("*")
          .eq("user_id", user!.id)
          .single();

        const userGoals: GoalColumns = goalsData || { weekly_goal_minutes: 0 };

        const logsByDate: Record<string, DailyLog[]> = {};
        (logs || []).forEach((log) => {
          if (!logsByDate[log.date]) logsByDate[log.date] = [];
          logsByDate[log.date].push(log);
        });

        const stats = computeStats(logsByDate, allDates, userGoals);

        // Overall Statistics
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Overall Statistics", 14, yPos);
        yPos += 3;
        yPos = renderStatsBox(doc, yPos, stats, brandCyan);
        yPos = renderWeeklySummaries(doc, yPos, stats.weeklySummaries, brandBlue);
        yPos = renderDailyTable(doc, yPos, stats.dailyRows, brandBlue, brandGreen);
      } else {
        // ====== CHILD MODE ======
        // Fetch children
        const { data: childRows, error: childErr } = await supabase
          .from("children")
          .select("id, name")
          .eq("user_id", user!.id)
          .order("name", { ascending: true });

        if (childErr) throw childErr;
        const children: ChildRecord[] = childRows || [];

        if (children.length === 0) {
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(12);
          doc.text("No children found.", 14, yPos);
        } else {
          // Fetch all child logs in one query
          const childIds = children.map((c) => c.id);
          const { data: allChildLogs, error: clErr } = await supabase
            .from("child_logs")
            .select("child_id, date, time_spent_minutes, daily_goal_at_time, weekly_goal_at_time")
            .in("child_id", childIds)
            .gte("date", startDate)
            .lte("date", endDate)
            .order("date", { ascending: true });

          if (clErr) throw clErr;

          // Fetch all child goals in one query
          const { data: allChildGoals } = await supabase
            .from("child_goals")
            .select("child_id, weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes")
            .in("child_id", childIds);

          // Group logs by child
          const logsByChild: Record<string, ChildLog[]> = {};
          (allChildLogs || []).forEach((log) => {
            if (!logsByChild[log.child_id]) logsByChild[log.child_id] = [];
            logsByChild[log.child_id].push(log as ChildLog);
          });

          // Goals by child
          const goalsByChild: Record<string, GoalColumns> = {};
          (allChildGoals || []).forEach((g: any) => {
            goalsByChild[g.child_id] = g;
          });

          // Render each child as a separate section
          for (let ci = 0; ci < children.length; ci++) {
            const child = children[ci];
            const childGoals: GoalColumns = goalsByChild[child.id] || { weekly_goal_minutes: 0 };
            const childLogs = logsByChild[child.id] || [];

            // Build logsByDate for this child
            const logsByDate: Record<string, DailyLog[]> = {};
            childLogs.forEach((log) => {
              if (!logsByDate[log.date]) logsByDate[log.date] = [];
              logsByDate[log.date].push(log);
            });

            const stats = computeStats(logsByDate, allDates, childGoals);

            // Child section separator — new page for each child (except first)
            if (ci > 0) {
              doc.addPage();
              yPos = 20;
            }

            // Child name header with colored bar
            doc.setTextColor(0, 0, 0);
            doc.setFillColor(...brandCyan);
            doc.rect(14, yPos - 4, pageWidth - 28, 12, "F");
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.text(child.name, 20, yPos + 3);
            yPos += 14;

            // Statistics
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text("Statistics", 14, yPos);
            yPos += 3;
            yPos = renderStatsBox(doc, yPos, stats, brandCyan);
            yPos = renderWeeklySummaries(doc, yPos, stats.weeklySummaries, brandBlue);
            yPos = renderDailyTable(doc, yPos, stats.dailyRows, brandBlue, brandGreen);
          }
        }
      }

      // Footer on all pages
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Amblyfy Progress Report — Page ${i} of ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 8,
          { align: "center" }
        );
        doc.text(
          "This report is generated for healthcare purposes only.",
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 4,
          { align: "center" }
        );
      }

      // Save PDF
      const fileName = `Amblyfy_Report_${startDate}_to_${endDate}.pdf`;
      doc.save(fileName);

      incrementPdfUsage();
      setReportsUsedToday(getPdfUsageToday());
      setSuccess("Report generated successfully!");
      setError("");
    } catch (err: any) {
      setError(err.message || "Failed to generate report");
      setSuccess("");
    } finally {
      setGenerating(false);
    }
  };

  const today = getLocalDateString();
  const remaining = MAX_REPORTS_PER_DAY - reportsUsedToday;

  return (
    <div className="mt-8">
      {/* Section Header */}
      <div
        className={`rounded-xl p-8 ${
          darkMode ? "bg-gray-800" : "bg-white"
        } shadow-lg border-l-4 border-brand-cyan`}
      >
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-brand-cyan to-brand-dark-green rounded-full flex items-center justify-center shadow-lg">
            <span className="text-3xl">📄</span>
          </div>
          <div>
            <h3
              className={`text-2xl font-bold ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Progress Report
            </h3>
            <p
              className={`text-base mt-1 ${
                darkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Generate a PDF report of your training statistics and activity
            </p>
          </div>
        </div>

        {/* Date Range Picker */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setError("");
                setSuccess("");
              }}
              min={firstEntryDate || undefined}
              max={today}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "border-gray-300 text-gray-900"
              }`}
            />
            {firstEntryDate && (
              <p
                className={`text-xs mt-1 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                First entry: {firstEntryDate}
              </p>
            )}
          </div>
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setError("");
                setSuccess("");
              }}
              min={startDate || firstEntryDate || undefined}
              max={today}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "border-gray-300 text-gray-900"
              }`}
            />
          </div>
        </div>

        {/* Usage info */}
        <div
          className={`flex items-center justify-between mb-4 p-3 rounded-lg ${
            darkMode ? "bg-gray-700" : "bg-gray-50"
          }`}
        >
          <span
            className={`text-sm ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Reports remaining today:
          </span>
          <span
            className={`text-sm font-bold ${
              remaining > 0
                ? darkMode
                  ? "text-green-400"
                  : "text-green-600"
                : darkMode
                ? "text-red-400"
                : "text-red-600"
            }`}
          >
            {remaining} / {MAX_REPORTS_PER_DAY}
          </span>
        </div>

        {/* Generate Button */}
        <button
          onClick={generateReport}
          disabled={generating || remaining <= 0}
          className="w-full bg-gradient-to-r from-brand-cyan to-brand-dark-green text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform-gpu hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {generating ? (
            <span className="flex items-center justify-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Generating Report...</span>
            </span>
          ) : (
            <span className="flex items-center justify-center space-x-2">
              <span>📥</span>
              <span>Generate PDF Report</span>
            </span>
          )}
        </button>

        {/* Status Messages */}
        {error && (
          <div
            className={`mt-4 p-4 rounded-lg text-sm font-medium ${
              darkMode
                ? "bg-red-900/30 text-red-300 border border-red-700"
                : "bg-red-50 text-red-800 border border-red-200"
            }`}
          >
            {error}
          </div>
        )}
        {success && (
          <div
            className={`mt-4 p-4 rounded-lg text-sm font-medium ${
              darkMode
                ? "bg-green-900/30 text-green-300 border border-green-700"
                : "bg-green-50 text-green-800 border border-green-200"
            }`}
          >
            {success}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressReport;
